// Third party
import React, { Component } from 'react';
import { connect } from 'react-redux';

// Types and Enums
import { TwoFactorEnableSteps } from '../../../../redux/modules/types/two-factor-auth';

// Redux
import { ReduxState } from '../../../../redux/modules/types';

// Actions
import { getNewMFAConfig, postConfirmationCode } from '../../../../redux/modules/two-factor-auth';

// Components
import BeginStep from './steps/BeginStep';
import SetupStep from './steps/SetupStep';
import ConfirmationStep from './steps/ConfirmationStep';
import EndStep from './steps/EndStep';
import Locale from '@/types/locale';

type StateProps = {
  loading: boolean;
  qrcode: string;
  secret: string;
  backup: string;
  uri?: string;
  locale: Locale;
  startStep: TwoFactorEnableSteps;
};

type DispatchProps = {
  getNewMFAConfig: () => Promise<void>;
  postConfirmationCode: (otp: string) => Promise<void>;
};

type Props = DispatchProps &
  StateProps & {
    gracePeriod?: number;
    initStep?: TwoFactorEnableSteps;
    initStepFeedback?: React.ReactNode;
    onConfirmationCodeSubmitted?: () => void;
    onDone: () => void;
    onCancel?: () => void;
  };

type State = {
  currentStep: TwoFactorEnableSteps;
};

export class TwoFactorAuthFlow extends Component<Props, State> {
  readonly state: State = {
    currentStep: this.props.startStep,
  };

  componentDidMount() {
    const { initStep, startStep } = this.props;
    if (initStep) {
      this.setState({ currentStep: initStep });
    }

    if (startStep === TwoFactorEnableSteps.QRCODE) {
      return this.props.getNewMFAConfig();
    }
  }

  getStarted = async () => {
    await this.props.getNewMFAConfig();
    this.setState({ currentStep: TwoFactorEnableSteps.QRCODE });
  };

  submitConfirmationCode = async (otp: string) => {
    await this.props.postConfirmationCode(otp);
    this.setState({ currentStep: TwoFactorEnableSteps.END });
    if (this.props.onConfirmationCodeSubmitted) {
      this.props.onConfirmationCodeSubmitted();
    }
  };

  getBeginStepVariant = () => {
    const { currentStep } = this.state;
    switch (currentStep) {
      case TwoFactorEnableSteps.MANDATORY_SETUP:
        return 'MandatorySetup';
      default:
        return 'Default';
    }
  };

  getEndStepVariant = () => {
    const { currentStep } = this.state;
    switch (currentStep) {
      case TwoFactorEnableSteps.BACKUP_LOGIN:
        return 'BackupLogin';
      case TwoFactorEnableSteps.NEW_BACKUP:
        return 'NewBackup';
      default:
        return 'Default';
    }
  };

  renderStep = () => {
    const { initStepFeedback, gracePeriod, onCancel, qrcode, secret, backup, loading } = this.props;
    const { currentStep } = this.state;
    const helpUrl = `${import.meta.env.REACT_APP_HELP_SITE_URL}/${this.props.locale.toLocaleLowerCase()}/articles/9315570206483`;

    switch (currentStep) {
      case TwoFactorEnableSteps.BEGIN:
      case TwoFactorEnableSteps.MANDATORY_SETUP: {
        return (
          <BeginStep
            feedback={initStepFeedback}
            gracePeriod={gracePeriod}
            helpUrl={helpUrl}
            loading={loading}
            onCancel={onCancel}
            primaryAction={this.getStarted}
            variant={this.getBeginStepVariant()}
          />
        );
      }
      case TwoFactorEnableSteps.QRCODE: {
        return (
          <SetupStep
            gracePeriod={gracePeriod}
            helpUrl={helpUrl}
            loading={loading}
            onCancel={onCancel}
            primaryAction={() => this.setState({ currentStep: TwoFactorEnableSteps.CONFIRMATION })}
            qrcode={qrcode}
            secret={secret}
          />
        );
      }
      case TwoFactorEnableSteps.CONFIRMATION: {
        return (
          <ConfirmationStep
            gracePeriod={gracePeriod}
            helpUrl={helpUrl}
            loading={loading}
            onCancel={onCancel}
            primaryAction={this.submitConfirmationCode}
            goToStep={() => this.setState({ currentStep: TwoFactorEnableSteps.QRCODE })}
          />
        );
      }
      case TwoFactorEnableSteps.END:
      case TwoFactorEnableSteps.BACKUP_LOGIN:
      case TwoFactorEnableSteps.NEW_BACKUP: {
        return <EndStep backup={backup} primaryAction={this.props.onDone} variant={this.getEndStepVariant()} />;
      }
    }
  };

  render() {
    return this.renderStep();
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  qrcode: state.twoFactorAuth.qrcode,
  secret: state.twoFactorAuth.secret,
  backup: state.twoFactorAuth.backup,
  loading: state.twoFactorAuth.loading,
  startStep: state.twoFactorAuth.startStep,
  locale: state.intl.locale,
});

export default connect(mapStateToProps, { getNewMFAConfig, postConfirmationCode })(TwoFactorAuthFlow);
